import { Grid, TableRow } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ColorPickerWidget,
  NumberInputWidget,
  SelectDropdownWidget,
} from 'src/components/widget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import { useGetProjectConfig } from 'src/hooks';
import { ProjectConfigInterface } from 'src/interfaces';
import { lang } from 'src/utils';

import { generateDropDownOptions } from '../../EditAccessibilityPage.helper';
import {
  StyledInputContainer,
  StyledTableCell,
} from '../../EditAccessibilityPage.styled';
import { AccessibilitySettingsItemProps } from './AccessibilitySettingsItem.interface';

export const AccessibilitySettingsItem = (
  props: AccessibilitySettingsItemProps,
) => {
  const { control } = useFormContext<Partial<ProjectConfigInterface>>();
  const { isViewOnly } = props;

  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];
  const mappedFonts =
    projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;

  return (
    <TableRow>
      <StyledTableCell width="30%">{props.label}</StyledTableCell>

      <StyledTableCell>
        <Grid container spacing={6}>
          {props.colorPath && (
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                key={props.colorPath}
                name={props.colorPath}
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <ColorPickerWidget
                      {...field}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? lang.errors.required : false}
                      disabled={isViewOnly}
                      colorPickerMode={props.colorPickerMode}
                    />
                  );
                }}
              />
            </Grid>
          )}

          {props.fontFamilyPath && (
            <Grid item xs={3}>
              <Controller
                key={props.fontFamilyPath}
                name={props.fontFamilyPath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      selectOptions={mappedFonts}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? lang.errors.required : false}
                      options={{ placeholder: lang.fontFamily }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}

          {props.fontSizePath && (
            <Grid item xs={2}>
              <Controller
                key={props.fontSizePath}
                name={props.fontSizePath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      selectOptions={generateDropDownOptions()}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? lang.errors.required : false}
                      options={{ placeholder: lang.fontSize }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}

          {props.lineHeightPath ? (
            <Grid item xs={2}>
              <Controller
                key={props.lineHeightPath}
                name={props.lineHeightPath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      value={field.value}
                      selectOptions={generateDropDownOptions()}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? lang.errors.required : false}
                      options={{ placeholder: lang.lineHeight }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          ) : (
            <Grid item xs={2} />
          )}

          {props.heightPath ? (
            <Grid item xs={2}>
              <Controller
                key={props.heightPath}
                name={props.heightPath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <NumberInputWidget
                      {...field}
                      onChange={(value) => {
                        if (value && Number(value) >= 1) {
                          field.onChange(value);
                        }
                      }}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      options={{ min: 1, placeholder: lang.height }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          ) : (
            <Grid item xs={2} />
          )}

          {props.backgroundPath && (
            <Grid item xs={2}>
              <Controller
                key={props.backgroundPath}
                name={props.backgroundPath}
                control={control}
                render={({ field, fieldState }) => (
                  <ColorPickerWidget
                    {...field}
                    label={lang.backgroundColor}
                    options={{
                      backgroundColor: true,
                      hideAIcon: true,
                      border: true,
                    }}
                    hasError={!!fieldState.error}
                    error={fieldState.error ? lang.errors.required : false}
                    disabled={isViewOnly}
                    colorPickerMode={props.colorPickerMode}
                  />
                )}
              />
            </Grid>
          )}

          {props.borderColorPath && (
            <Grid item xs={3}>
              <Controller
                key={props.borderColorPath}
                name={props.borderColorPath}
                control={control}
                render={({ field, fieldState }) => (
                  <ColorPickerWidget
                    {...field}
                    label={lang.borderColor}
                    hasError={Boolean(fieldState.error?.message)}
                    error={fieldState.error?.message}
                    options={{
                      backgroundColor: true,
                      hideAIcon: true,
                      border: true,
                    }}
                    disabled={isViewOnly}
                    colorPickerMode={props.colorPickerMode}
                  />
                )}
              />
            </Grid>
          )}

          {props.borderStylePath && (
            <Grid item xs={2}>
              <Controller
                key={props.borderStylePath}
                name={props.borderStylePath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      label={lang.borderStyle}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      selectOptions={[
                        {
                          label: 'solid',
                          value: 'solid',
                        },
                        {
                          label: 'dotted',
                          value: 'dotted',
                        },
                        {
                          label: 'dashed',
                          value: 'dashed',
                        },
                      ]}
                      options={{ placeholder: lang.borderStyle }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}

          {props.borderWidthPath && (
            <Grid item xs={2}>
              <Controller
                key={props.borderWidthPath}
                name={props.borderWidthPath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      label={lang.strokeWeight}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      selectOptions={generateDropDownOptions()}
                      options={{ placeholder: lang.strokeWeight }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}

          {props.borderRadiusPath && (
            <Grid item xs={2}>
              <Controller
                key={props.borderRadiusPath}
                name={props.borderRadiusPath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      label={lang.borderRadius}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      selectOptions={generateDropDownOptions()}
                      options={{ placeholder: lang.borderRadius }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}

          {props.barWidthPath && (
            <Grid item xs={3}>
              <Controller
                key={props.barWidthPath}
                name={props.barWidthPath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      label={lang.barWidth}
                      selectOptions={generateDropDownOptions()}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? lang.errors.required : false}
                      options={{ placeholder: lang.barWidth }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}

          {props.sizePath && (
            <Grid item xs={2}>
              <Controller
                key={props.sizePath}
                name={props.sizePath}
                control={control}
                render={({ field, fieldState }) => (
                  <StyledInputContainer>
                    <SelectDropdownWidget
                      {...field}
                      label={lang.badgeSize}
                      selectOptions={generateDropDownOptions()}
                      hasError={!!fieldState.error}
                      error={fieldState.error ? lang.errors.required : false}
                      options={{ placeholder: lang.badgeSize }}
                      disabled={isViewOnly}
                    />
                  </StyledInputContainer>
                )}
              />
            </Grid>
          )}
        </Grid>
      </StyledTableCell>
    </TableRow>
  );
};
